/**
 * Câu hỏi đơn giản cho DoorQuest Game
 * Tập trung vào: ký hiệu, công thức, đơn vị, ý nghĩa cơ bản
 */

import { DoorQuestQuestion } from '../types'

interface MinigameFileContent {
  questions?: DoorQuestQuestion[]
  config?: {
    minQuestions?: number
    maxQuestions?: number
  }
}

const rawMinigameModules = import.meta.glob('../du-lieu/noi-dung/**/minigame.json', {
  eager: true,
  import: 'default'
}) as Record<string, MinigameFileContent>

const minigameQuestionMap: Record<string, DoorQuestQuestion[]> = {}
const minigameConfigMap: Record<string, { minQuestions?: number; maxQuestions?: number }> = {}

const lessonKeyFromPath = (path: string): string | null => {
  const normalized = path.replace('../du-lieu/noi-dung/', '')
  const segments = normalized.split('/')
  if (segments.length < 3) {
    return null
  }

  const lopMatch = segments[0]?.match(/lop-(\d+)/)
  const chuongMatch = segments[1]?.match(/chuong-(\d+)/)
  const baiMatch = segments[2]?.match(/bai-(\d+)/)

  if (!lopMatch || !chuongMatch || !baiMatch) {
    return null
  }

  return `lop-${lopMatch[1]}-chuong-${chuongMatch[1]}-bai-${baiMatch[1]}`
}

Object.entries(rawMinigameModules).forEach(([path, data]) => {
  const key = lessonKeyFromPath(path)
  if (!key || !data) {
    return
  }

  if (Array.isArray(data as any)) {
    // Support legacy format: export default [...] (array of questions)
    minigameQuestionMap[key] = data as unknown as DoorQuestQuestion[]
    return
  }

  if (Array.isArray(data.questions)) {
    minigameQuestionMap[key] = data.questions
  }

  if (data.config) {
    minigameConfigMap[key] = data.config
  }
})

interface GameQuestionsData {
  [key: string]: DoorQuestQuestion[] // key format: "lop-{lop}-chuong-{chuong}-bai-{bai}"
}

/**
 * Database câu hỏi game đơn giản cho từng bài học
 */
export const gameQuestions: GameQuestionsData = {
  // ========== LỚP 10 ==========
  
  'lop-10-chuong-1-bai-1': [
    {
      id: 'l10-c1-b1-q1',
      question: 'Đơn vị vận tốc SI?',
      answers: ['m/s', 'km/h', 'cm/s'],
      correctIndex: 0,
      explanation: 'Vận tốc SI: m/s'
    },
    {
      id: 'l10-c1-b1-q2',
      question: 'Gia tốc kí hiệu là?',
      answers: ['v', 'a', 's'],
      correctIndex: 1,
      explanation: 'Gia tốc kí hiệu là a'
    },
    {
      id: 'l10-c1-b1-q3',
      question: 'Công thức v trung bình?',
      answers: ['v = s/t', 'v = at', 'v = st'],
      correctIndex: 0,
      explanation: 'Vận tốc trung bình: v = s/t'
    },
    {
      id: 'l10-c1-b1-q4',
      question: 'Đơn vị gia tốc?',
      answers: ['m/s', 'm/s²', 'm²/s'],
      correctIndex: 1,
      explanation: 'Gia tốc có đơn vị m/s²'
    },
    {
      id: 'l10-c1-b1-q5',
      question: 'Chuyển động đều có?',
      answers: ['v = const', 'a = const', 's = const'],
      correctIndex: 0,
      explanation: 'Chuyển động đều: v không đổi'
    },
    {
      id: 'l10-c1-b1-q6',
      question: 'Quãng đường kí hiệu là?',
      answers: ['v', 't', 's'],
      correctIndex: 2,
      explanation: 'Quãng đường kí hiệu là s'
    },
    {
      id: 'l10-c1-b1-q7',
      question: 'Thời gian kí hiệu là?',
      answers: ['t', 's', 'T'],
      correctIndex: 0,
      explanation: 'Thời gian kí hiệu là t'
    },
    {
      id: 'l10-c1-b1-q8',
      question: 'Đơn vị thời gian SI?',
      answers: ['phút', 'giờ', 's'],
      correctIndex: 2,
      explanation: 'Thời gian SI: giây (s)'
    },
    {
      id: 'l10-c1-b1-q9',
      question: 'Công thức a?',
      answers: ['a = Δv/Δt', 'a = v/t', 'a = s/t'],
      correctIndex: 0,
      explanation: 'Gia tốc: a = Δv/Δt'
    },
    {
      id: 'l10-c1-b1-q10',
      question: 'Vận tốc kí hiệu là?',
      answers: ['v', 'a', 's'],
      correctIndex: 0,
      explanation: 'Vận tốc kí hiệu là v'
    }
  ],

  // Lớp 10 - Bài 2: Độ dịch chuyển
'lop-10-chuong-1-bai-2': [],

  // Lớp 10 - Bài 3: Tốc độ và vận tốc
'lop-10-chuong-1-bai-3': [],

  // Lớp 10 - Bài 5: Chuyển động biến đổi
'lop-10-chuong-1-bai-5': [],

  // ========== LỚP 11 ==========

  // Chương 1: Dao động
  'lop-11-chuong-1-bai-1': [
    {
      id: 'l11-c1-b1-q1',
      question: 'Biên độ kí hiệu là?',
      answers: ['f', 'A', 'T'],
      correctIndex: 1,
      explanation: 'Biên độ kí hiệu là A'
    },
    {
      id: 'l11-c1-b1-q2',
      question: 'Đơn vị của chu kỳ?',
      answers: ['Hz', 's', 'm'],
      correctIndex: 1,
      explanation: 'Chu kỳ T có đơn vị là giây (s)'
    },
    {
      id: 'l11-c1-b1-q3',
      question: 'Công thức T và f?',
      answers: ['T = 1/f', 'T = f', 'T = 2πf'],
      correctIndex: 0,
      explanation: 'Chu kỳ và tần số: T = 1/f'
    },
    {
      id: 'l11-c1-b1-q4',
      question: 'Đơn vị của tần số?',
      answers: ['s', 'Hz', 'm/s'],
      correctIndex: 1,
      explanation: 'Tần số f có đơn vị là Hertz (Hz)'
    },
    {
      id: 'l11-c1-b1-q5',
      question: 'Tần số góc kí hiệu là?',
      answers: ['ω', 'ϕ', 'A'],
      correctIndex: 0,
      explanation: 'Tần số góc kí hiệu là ω (omega)'
    },
    {
      id: 'l11-c1-b1-q6',
      question: 'Phương trình dao động?',
      answers: ['x = Asin(ωt+ϕ)', 'x = At', 'x = ωt'],
      correctIndex: 0,
      explanation: 'Phương trình: x = Asin(ωt + ϕ)'
    },
    {
      id: 'l11-c1-b1-q7',
      question: 'Li độ cực đại là?',
      answers: ['v max', 'A', 'ω'],
      correctIndex: 1,
      explanation: 'Li độ cực đại chính là biên độ A'
    },
    {
      id: 'l11-c1-b1-q8',
      question: 'Công thức ω theo T?',
      answers: ['ω = 2π/T', 'ω = T/2π', 'ω = 2πT'],
      correctIndex: 0,
      explanation: 'Tần số góc: ω = 2π/T (rad/s)'
    },
    {
      id: 'l11-c1-b1-q9',
      question: 'Đơn vị của ω?',
      answers: ['rad/s', 'Hz', 's'],
      correctIndex: 0,
      explanation: 'Tần số góc ω có đơn vị rad/s'
    },
    {
      id: 'l11-c1-b1-q10',
      question: 'Pha của dao động là?',
      answers: ['ω.t + ϕ', 'ϕ', 'ω.t'],
      correctIndex: 0,
      explanation: 'Pha dao động: ω.t + ϕ'
    }
  ],

  'lop-11-chuong-1-bai-3': [
    {
      id: 'l11-c1-b3-q1',
      question: 'Động năng kí hiệu là?',
      answers: ['Wđ', 'Wt', 'W'],
      correctIndex: 0,
      explanation: 'Động năng kí hiệu là Wđ'
    },
    {
      id: 'l11-c1-b3-q2',
      question: 'Thế năng kí hiệu là?',
      answers: ['Wđ', 'Wt', 'F'],
      correctIndex: 1,
      explanation: 'Thế năng kí hiệu là Wt'
    },
    {
      id: 'l11-c1-b3-q3',
      question: 'Công thức cơ năng?',
      answers: ['W = kA²', 'W = ½kA²', 'W = mA²'],
      correctIndex: 1,
      explanation: 'Cơ năng: W = ½kA² (không đổi)'
    },
    {
      id: 'l11-c1-b3-q4',
      question: 'Cơ năng có đặc điểm gì?',
      answers: ['Biến đổi', 'Bảo toàn', 'Tăng dần'],
      correctIndex: 1,
      explanation: 'Cơ năng dao động điều hòa được bảo toàn'
    },
    {
      id: 'l11-c1-b3-q5',
      question: 'Khi Wđ = Wt thì x = ?',
      answers: ['±A', '±A√2/2', '0'],
      correctIndex: 1,
      explanation: 'Khi động năng bằng thế năng: x = ±A√2/2'
    },
    {
      id: 'l11-c1-b3-q6',
      question: 'Ở biên, Wđ bằng?',
      answers: ['W', '0', 'W/2'],
      correctIndex: 1,
      explanation: 'Ở biên: v=0 nên Wđ=0'
    },
    {
      id: 'l11-c1-b3-q7',
      question: 'Ở VTCB, Wt bằng?',
      answers: ['W', '0', 'W/2'],
      correctIndex: 1,
      explanation: 'Ở VTCB: x=0 nên Wt=0'
    },
    {
      id: 'l11-c1-b3-q8',
      question: 'Đơn vị năng lượng?',
      answers: ['N', 'J', 'W'],
      correctIndex: 1,
      explanation: 'Năng lượng có đơn vị Joule (J)'
    },
    {
      id: 'l11-c1-b3-q9',
      question: 'Công thức Wđ?',
      answers: ['Wđ = ½mv²', 'Wđ = mv', 'Wđ = mv²'],
      correctIndex: 0,
      explanation: 'Động năng: Wđ = ½mv²'
    },
    {
      id: 'l11-c1-b3-q10',
      question: 'Công thức Wt lò xo?',
      answers: ['Wt = kx²', 'Wt = ½kx²', 'Wt = ½kx'],
      correctIndex: 1,
      explanation: 'Thế năng lò xo: Wt = ½kx²'
    }
  ],

  // Lớp 11 - Bài 4: Dao động tắt dần
  'lop-11-chuong-1-bai-4': [
    {
      id: 'l11-c1-b4-q1',
      question: 'Dao động tắt dần do?',
      answers: ['Lực ma sát', 'Trọng lực', 'Lực đàn hồi'],
      correctIndex: 0,
      explanation: 'Dao động tắt dần do lực ma sát'
    },
    {
      id: 'l11-c1-b4-q2',
      question: 'Dao động tắt dần, A?',
      answers: ['Tăng', 'Giảm', 'Không đổi'],
      correctIndex: 1,
      explanation: 'Dao động tắt dần: A giảm dần'
    },
    {
      id: 'l11-c1-b4-q3',
      question: 'Dao động cưỡng bức là?',
      answers: ['Tự do', 'Chịu lực tuần hoàn', 'Tắt dần'],
      correctIndex: 1,
      explanation: 'Dao động cưỡng bức: chịu ngoại lực tuần hoàn'
    },
    {
      id: 'l11-c1-b4-q4',
      question: 'Cộng hưởng xảy ra khi?',
      answers: ['f = f₀', 'f ≠ f₀', 'A = 0'],
      correctIndex: 0,
      explanation: 'Cộng hưởng: f ngoại lực = f₀ riêng'
    },
    {
      id: 'l11-c1-b4-q5',
      question: 'Cộng hưởng, A sẽ?',
      answers: ['Cực đại', 'Cực tiểu', 'Không đổi'],
      correctIndex: 0,
      explanation: 'Cộng hưởng: A đạt cực đại'
    },
    {
      id: 'l11-c1-b4-q6',
      question: 'Dao động duy trì là?',
      answers: ['Tự do', 'Bù năng lượng', 'Tắt dần'],
      correctIndex: 1,
      explanation: 'Dao động duy trì: bù năng lượng mất đi'
    },
    {
      id: 'l11-c1-b4-q7',
      question: 'Tần số dao động cưỡng bức?',
      answers: ['f₀', 'f ngoại lực', 'f bất kỳ'],
      correctIndex: 1,
      explanation: 'Tần số = tần số ngoại lực'
    },
    {
      id: 'l11-c1-b4-q8',
      question: 'Ví dụ cộng hưởng?',
      answers: ['Đàn guitar', 'Quả bóng', 'Xe đạp'],
      correctIndex: 0,
      explanation: 'Đàn guitar: hộp cộng hưởng'
    },
    {
      id: 'l11-c1-b4-q9',
      question: 'Ma sát làm gì với A?',
      answers: ['Tăng A', 'Giảm A', 'Giữ A'],
      correctIndex: 1,
      explanation: 'Ma sát làm giảm A (tắt dần)'
    },
    {
      id: 'l11-c1-b4-q10',
      question: 'Tần số riêng kí hiệu?',
      answers: ['f', 'f₀', 'F'],
      correctIndex: 1,
      explanation: 'Tần số riêng: f₀'
    }
  ],

  // Chương 2: Sóng
  'lop-11-chuong-2-bai-5': [
    {
      id: 'l11-c2-b5-q1',
      question: 'Bước sóng kí hiệu là gì?',
      answers: ['A', 'λ', 'f'],
      correctIndex: 1,
      explanation: 'Bước sóng kí hiệu là λ (lambda)'
    },
    {
      id: 'l11-c2-b5-q2',
      question: 'Đơn vị của bước sóng?',
      answers: ['m', 'Hz', 's'],
      correctIndex: 0,
      explanation: 'Bước sóng λ có đơn vị là mét (m)'
    },
    {
      id: 'l11-c2-b5-q3',
      question: 'Công thức liên hệ v, λ, f?',
      answers: ['v = λ/f', 'v = λ.f', 'v = λ+f'],
      correctIndex: 1,
      explanation: 'Công thức sóng: v = λ.f'
    },
    {
      id: 'l11-c2-b5-q4',
      question: 'Đơn vị của tần số?',
      answers: ['m/s', 'Hz', 'm'],
      correctIndex: 1,
      explanation: 'Tần số f có đơn vị là Hertz (Hz)'
    },
    {
      id: 'l11-c2-b5-q5',
      question: 'Khoảng cách hai đỉnh sóng gọi là gì?',
      answers: ['Biên độ', 'Bước sóng', 'Chu kỳ'],
      correctIndex: 1,
      explanation: 'Khoảng cách giữa hai đỉnh sóng liên tiếp là bước sóng λ'
    },
    {
      id: 'l11-c2-b5-q6',
      question: 'Công thức liên hệ T và f?',
      answers: ['T = f', 'T = 1/f', 'T = 2πf'],
      correctIndex: 1,
      explanation: 'Chu kỳ và tần số: T = 1/f'
    },
    {
      id: 'l11-c2-b5-q7',
      question: 'Vận tốc sóng phụ thuộc vào?',
      answers: ['Tần số nguồn', 'Môi trường', 'Biên độ'],
      correctIndex: 1,
      explanation: 'Vận tốc truyền sóng phụ thuộc môi trường truyền'
    },
    {
      id: 'l11-c2-b5-q8',
      question: 'Biên độ sóng kí hiệu là gì?',
      answers: ['T', 'A', 'v'],
      correctIndex: 1,
      explanation: 'Biên độ sóng kí hiệu là A'
    },
    {
      id: 'l11-c2-b5-q9',
      question: 'Tăng f thì λ sẽ?',
      answers: ['Tăng', 'Giảm', 'Không đổi'],
      correctIndex: 1,
      explanation: 'Khi v không đổi, tăng f thì λ giảm'
    },
    {
      id: 'l11-c2-b5-q10',
      question: 'Pha của dao động là?',
      answers: ['ω.t + ϕ', 'ϕ', 'ω.t'],
      correctIndex: 0,
      explanation: 'Pha dao động: ω.t + ϕ'
    }
  ],

  // Chương 1: Bài 2 - Con lắc lò xo
  'lop-11-chuong-1-bai-2': [
    {
      id: 'l11-c1-b2-q1',
      question: 'Độ cứng lò xo kí hiệu là?',
      answers: ['k', 'm', 'F'],
      correctIndex: 0,
      explanation: 'Độ cứng lò xo kí hiệu là k'
    },
    {
      id: 'l11-c1-b2-q2',
      question: 'Đơn vị của k?',
      answers: ['N', 'N/m', 'kg'],
      correctIndex: 1,
      explanation: 'Độ cứng k có đơn vị N/m'
    },
    {
      id: 'l11-c1-b2-q3',
      question: 'Công thức ω lò xo?',
      answers: ['ω = √(k/m)', 'ω = √(m/k)', 'ω = k/m'],
      correctIndex: 0,
      explanation: 'Tần số góc: ω = √(k/m)'
    },
    {
      id: 'l11-c1-b2-q4',
      question: 'Công thức T lò xo?',
      answers: ['T = 2π√(m/k)', 'T = 2π√(k/m)', 'T = √(m/k)'],
      correctIndex: 0,
      explanation: 'Chu kỳ: T = 2π√(m/k)'
    },
    {
      id: 'l11-c1-b2-q5',
      question: 'Tăng m thì T?',
      answers: ['Tăng', 'Giảm', 'Không đổi'],
      correctIndex: 0,
      explanation: 'T tỉ lệ với √m, tăng m thì T tăng'
    },
    {
      id: 'l11-c1-b2-q6',
      question: 'Tăng k thì T?',
      answers: ['Tăng', 'Giảm', 'Không đổi'],
      correctIndex: 1,
      explanation: 'T tỉ lệ nghịch với √k, tăng k thì T giảm'
    },
    {
      id: 'l11-c1-b2-q7',
      question: 'Lực kéo về kí hiệu là?',
      answers: ['F', 'f', 'k'],
      correctIndex: 0,
      explanation: 'Lực kéo về kí hiệu là F'
    },
    {
      id: 'l11-c1-b2-q8',
      question: 'Công thức lực kéo về?',
      answers: ['F = -kx', 'F = kx', 'F = mx'],
      correctIndex: 0,
      explanation: 'Lực kéo về: F = -kx (dấu - chỉ hướng)'
    },
    {
      id: 'l11-c1-b2-q9',
      question: 'Ở VTCB, lực kéo về?',
      answers: ['Cực đại', 'Bằng 0', 'Cực tiểu'],
      correctIndex: 1,
      explanation: 'Ở VTCB: x=0 nên F=0'
    },
    {
      id: 'l11-c1-b2-q10',
      question: 'Ở biên, lực kéo về?',
      answers: ['Bằng 0', 'Cực đại', 'Cực tiểu'],
      correctIndex: 1,
      explanation: 'Ở biên: |x|=A nên |F| cực đại'
    }
  ],
  // Chương 2: Bài 6 - Sóng dọc và sóng ngang
  'lop-11-chuong-2-bai-6': [
    {
      id: 'l11-c2-b6-q1',
      question: 'Sóng dọc có phương dao động?',
      answers: ['// phương truyền', '⊥ phương truyền', 'Trùng với phương truyền'],
      correctIndex: 0,
      explanation: 'Sóng dọc: phương dao động // phương truyền'
    },
    {
      id: 'l11-c2-b6-q2',
      question: 'Sóng ngang có phương dao động?',
      answers: ['// phương truyền', '⊥ phương truyền', 'Trùng với phương truyền'],
      correctIndex: 1,
      explanation: 'Sóng ngang: phương dao động ⊥ phương truyền'
    },
    {
      id: 'l11-c2-b6-q3',
      question: 'Sóng dọc tạo ra?',
      answers: ['Đỉnh và đáy', 'Vùng nén và giãn', 'Bụng và nút'],
      correctIndex: 1,
      explanation: 'Sóng dọc tạo vùng nén và giãn'
    },
    {
      id: 'l11-c2-b6-q4',
      question: 'Sóng ngang tạo ra?',
      answers: ['Vùng nén và giãn', 'Đỉnh và đáy', 'Bụng và nút'],
      correctIndex: 1,
      explanation: 'Sóng ngang tạo đỉnh và đáy sóng'
    },
    {
      id: 'l11-c2-b6-q5',
      question: 'Sóng âm là sóng?',
      answers: ['Dọc', 'Ngang', 'Cả hai'],
      correctIndex: 0,
      explanation: 'Sóng âm là sóng dọc'
    },
    {
      id: 'l11-c2-b6-q6',
      question: 'Sóng trên dây là sóng?',
      answers: ['Dọc', 'Ngang', 'Cả hai'],
      correctIndex: 1,
      explanation: 'Sóng trên dây là sóng ngang'
    },
    {
      id: 'l11-c2-b6-q7',
      question: 'Sóng dọc truyền trong?',
      answers: ['Chỉ rắn', 'Rắn, lỏng, khí', 'Chỉ lỏng'],
      correctIndex: 1,
      explanation: 'Sóng dọc truyền trong rắn, lỏng, khí'
    },
    {
      id: 'l11-c2-b6-q8',
      question: 'Sóng ngang cơ truyền trong?',
      answers: ['Rắn, lỏng, khí', 'Rắn và bề mặt lỏng', 'Chỉ rắn'],
      correctIndex: 1,
      explanation: 'Sóng ngang cơ: rắn và bề mặt lỏng'
    },
    {
      id: 'l11-c2-b6-q9',
      question: 'Kí hiệu bước sóng?',
      answers: ['λ', 'f', 'v'],
      correctIndex: 0,
      explanation: 'Bước sóng kí hiệu là λ (lambda)'
    },
    {
      id: 'l11-c2-b6-q10',
      question: 'Đơn vị bước sóng?',
      answers: ['Hz', 'm', 'm/s'],
      correctIndex: 1,
      explanation: 'Bước sóng có đơn vị mét (m)'
    }
  ],

  // Chương 2: Bài 7 - Sóng điện từ
  'lop-11-chuong-2-bai-7': [
    {
      id: 'l11-c2-b7-q1',
      question: 'Tốc độ sóng điện từ trong chân không?',
      answers: ['3×10⁸ m/s', '3×10⁶ m/s', '3×10¹⁰ m/s'],
      correctIndex: 0,
      explanation: 'Tốc độ ánh sáng: c = 3×10⁸ m/s'
    },
    {
      id: 'l11-c2-b7-q2',
      question: 'Kí hiệu tốc độ ánh sáng?',
      answers: ['v', 'c', 'f'],
      correctIndex: 1,
      explanation: 'Tốc độ ánh sáng kí hiệu là c'
    },
    {
      id: 'l11-c2-b7-q3',
      question: 'Điện trường kí hiệu?',
      answers: ['E', 'B', 'c'],
      correctIndex: 0,
      explanation: 'Điện trường kí hiệu là E'
    },
    {
      id: 'l11-c2-b7-q4',
      question: 'Từ trường kí hiệu?',
      answers: ['E', 'B', 'c'],
      correctIndex: 1,
      explanation: 'Từ trường kí hiệu là B'
    },
    {
      id: 'l11-c2-b7-q5',
      question: 'E và B trong sóng điện từ?',
      answers: ['Song song', 'Vuông góc', 'Trùng nhau'],
      correctIndex: 1,
      explanation: 'E và B vuông góc với nhau'
    },
    {
      id: 'l11-c2-b7-q6',
      question: 'Sóng điện từ truyền trong?',
      answers: ['Chỉ vật chất', 'Chân không', 'Cả hai'],
      correctIndex: 2,
      explanation: 'Sóng điện từ truyền trong chân không và vật chất'
    },
    {
      id: 'l11-c2-b7-q7',
      question: 'Bước sóng tăng thì tần số?',
      answers: ['Tăng', 'Giảm', 'Không đổi'],
      correctIndex: 1,
      explanation: 'c = λf, tăng λ thì f giảm'
    },
    {
      id: 'l11-c2-b7-q8',
      question: 'Tia gamma có bước sóng?',
      answers: ['Dài nhất', 'Ngắn nhất', 'Trung bình'],
      correctIndex: 1,
      explanation: 'Tia gamma có bước sóng ngắn nhất'
    },
    {
      id: 'l11-c2-b7-q9',
      question: 'Sóng vô tuyến có bước sóng?',
      answers: ['Ngắn nhất', 'Dài nhất', 'Trung bình'],
      correctIndex: 1,
      explanation: 'Sóng vô tuyến có bước sóng dài nhất'
    },
    {
      id: 'l11-c2-b7-q10',
      question: 'Công thức c = ?',
      answers: ['λf', 'λ/f', 'f/λ'],
      correctIndex: 0,
      explanation: 'Tốc độ: c = λf'
    }
  ],

  // Chương 2: Bài 8 - Giao thoa sóng
  'lop-11-chuong-2-bai-8': [
    {
      id: 'l11-c2-b8-q1',
      question: 'Điều kiện sóng kết hợp?',
      answers: ['Cùng tần số, lệch pha không đổi', 'Khác tần số', 'Lệch pha thay đổi'],
      correctIndex: 0,
      explanation: 'Sóng kết hợp: cùng tần số, độ lệch pha không đổi'
    },
    {
      id: 'l11-c2-b8-q2',
      question: 'Cực đại giao thoa khi Δd = ?',
      answers: ['kλ', '(k+1/2)λ', 'kλ/2'],
      correctIndex: 0,
      explanation: 'Cực đại: Δd = kλ (k = 0, ±1, ±2...)'
    },
    {
      id: 'l11-c2-b8-q3',
      question: 'Cực tiểu giao thoa khi Δd = ?',
      answers: ['kλ', '(k+1/2)λ', 'kλ/2'],
      correctIndex: 1,
      explanation: 'Cực tiểu: Δd = (k + 1/2)λ'
    },
    {
      id: 'l11-c2-b8-q4',
      question: 'Kí hiệu hiệu đường đi?',
      answers: ['d', 'Δd', 'λ'],
      correctIndex: 1,
      explanation: 'Hiệu đường đi kí hiệu là Δd'
    },
    {
      id: 'l11-c2-b8-q5',
      question: 'Cực đại khi hai sóng?',
      answers: ['Ngược pha', 'Cùng pha', 'Lệch pha 90°'],
      correctIndex: 1,
      explanation: 'Cực đại khi hai sóng cùng pha'
    },
    {
      id: 'l11-c2-b8-q6',
      question: 'Cực tiểu khi hai sóng?',
      answers: ['Cùng pha', 'Ngược pha', 'Lệch pha 90°'],
      correctIndex: 1,
      explanation: 'Cực tiểu khi hai sóng ngược pha'
    },
    {
      id: 'l11-c2-b8-q7',
      question: 'Khoảng vân kí hiệu?',
      answers: ['i', 'λ', 'd'],
      correctIndex: 0,
      explanation: 'Khoảng vân kí hiệu là i'
    },
    {
      id: 'l11-c2-b8-q8',
      question: 'Công thức khoảng vân?',
      answers: ['i = λD/a', 'i = aD/λ', 'i = λa/D'],
      correctIndex: 0,
      explanation: 'Khoảng vân: i = λD/a'
    },
    {
      id: 'l11-c2-b8-q9',
      question: 'Tăng λ thì i?',
      answers: ['Tăng', 'Giảm', 'Không đổi'],
      correctIndex: 0,
      explanation: 'i tỉ lệ với λ, tăng λ thì i tăng'
    },
    {
      id: 'l11-c2-b8-q10',
      question: 'Giao thoa chứng minh tính chất?',
      answers: ['Hạt', 'Sóng', 'Cả hai'],
      correctIndex: 1,
      explanation: 'Giao thoa chứng minh tính chất sóng'
    }
  ],

  // Chương 2: Bài 9 - Sóng dừng
  'lop-11-chuong-2-bai-9': [
    {
      id: 'l11-c2-b9-q1',
      question: 'Khoảng cách hai nút liên tiếp?',
      answers: ['λ/2', 'λ', 'λ/4'],
      correctIndex: 0,
      explanation: 'Khoảng cách hai nút liên tiếp: λ/2'
    },
    {
      id: 'l11-c2-b9-q2',
      question: 'Khoảng cách nút - bụng liền kề?',
      answers: ['λ/2', 'λ/4', 'λ'],
      correctIndex: 1,
      explanation: 'Khoảng cách nút - bụng: λ/4'
    },
    {
      id: 'l11-c2-b9-q3',
      question: 'Dây hai đầu cố định: L = ?',
      answers: ['kλ/2', 'kλ', '(2k+1)λ/4'],
      correctIndex: 0,
      explanation: 'Dây hai đầu cố định: L = kλ/2'
    },
    {
      id: 'l11-c2-b9-q4',
      question: 'Dây một đầu cố định: L = ?',
      answers: ['kλ/2', 'kλ', '(2k+1)λ/4'],
      correctIndex: 2,
      explanation: 'Dây một đầu cố định: L = (2k+1)λ/4'
    },
    {
      id: 'l11-c2-b9-q5',
      question: 'Nút sóng có biên độ?',
      answers: ['Cực đại', 'Bằng 0', 'Trung bình'],
      correctIndex: 1,
      explanation: 'Nút sóng: biên độ bằng 0 (đứng yên)'
    },
    {
      id: 'l11-c2-b9-q6',
      question: 'Bụng sóng có biên độ?',
      answers: ['Bằng 0', 'Cực đại', 'Trung bình'],
      correctIndex: 1,
      explanation: 'Bụng sóng: biên độ cực đại'
    },
    {
      id: 'l11-c2-b9-q7',
      question: 'Số bụng = số nút khi?',
      answers: ['Luôn luôn', 'Dây hai đầu cố định', 'Không bao giờ'],
      correctIndex: 2,
      explanation: 'Số bụng luôn khác số nút (nút nhiều hơn 1)'
    },
    {
      id: 'l11-c2-b9-q8',
      question: 'Tăng λ thì số bụng?',
      answers: ['Tăng', 'Giảm', 'Không đổi'],
      correctIndex: 1,
      explanation: 'L = kλ/2, tăng λ thì k giảm → số bụng giảm'
    },
    {
      id: 'l11-c2-b9-q9',
      question: 'Sóng dừng truyền năng lượng?',
      answers: ['Có', 'Không', 'Một phần'],
      correctIndex: 1,
      explanation: 'Sóng dừng không truyền năng lượng ra xa'
    },
    {
      id: 'l11-c2-b9-q10',
      question: 'Kí hiệu chiều dài dây?',
      answers: ['L', 'λ', 'k'],
      correctIndex: 0,
      explanation: 'Chiều dài dây kí hiệu là L'
    }
  ],

  // ========== LỚP 12 ==========
  
  'lop-12-chuong-1-bai-1': [
    {
      id: 'l12-c1-b1-q1',
      question: 'Đơn vị nhiệt độ tuyệt đối?',
      answers: ['°C', 'K', 'F'],
      correctIndex: 1,
      explanation: 'Nhiệt độ tuyệt đối: Kelvin (K)'
    },
    {
      id: 'l12-c1-b1-q2',
      question: 'Công thức chuyển °C sang K?',
      answers: ['T = t + 273', 'T = t - 273', 'T = 273/t'],
      correctIndex: 0,
      explanation: 'Chuyển đổi: T(K) = t(°C) + 273'
    },
    {
      id: 'l12-c1-b1-q3',
      question: 'Nhiệt lượng kí hiệu là?',
      answers: ['Q', 'W', 'P'],
      correctIndex: 0,
      explanation: 'Nhiệt lượng kí hiệu là Q'
    },
    {
      id: 'l12-c1-b1-q4',
      question: 'Đơn vị nhiệt dung riêng?',
      answers: ['J/kg', 'J/(kg.K)', 'J/K'],
      correctIndex: 1,
      explanation: 'Nhiệt dung riêng c: J/(kg.K)'
    },
    {
      id: 'l12-c1-b1-q5',
      question: 'Công thức nhiệt lượng?',
      answers: ['Q = mcΔt', 'Q = m/cΔt', 'Q = mc/Δt'],
      correctIndex: 0,
      explanation: 'Nhiệt lượng: Q = mcΔt'
    },
    {
      id: 'l12-c1-b1-q6',
      question: 'Nhiệt dung riêng kí hiệu?',
      answers: ['c', 'Q', 'm'],
      correctIndex: 0,
      explanation: 'Nhiệt dung riêng kí hiệu là c'
    },
    {
      id: 'l12-c1-b1-q7',
      question: 'Đơn vị nhiệt lượng?',
      answers: ['W', 'J', 'N'],
      correctIndex: 1,
      explanation: 'Nhiệt lượng có đơn vị Joule (J)'
    },
    {
      id: 'l12-c1-b1-q8',
      question: '0°C bằng bao nhiêu K?',
      answers: ['273K', '0K', '100K'],
      correctIndex: 0,
      explanation: '0°C = 273K'
    },
    {
      id: 'l12-c1-b1-q9',
      question: 'Khối lượng kí hiệu là?',
      answers: ['m', 'M', 'μ'],
      correctIndex: 0,
      explanation: 'Khối lượng kí hiệu là m'
    },
    {
      id: 'l12-c1-b1-q10',
      question: 'Đơn vị khối lượng SI?',
      answers: ['g', 'kg', 'tấn'],
      correctIndex: 1,
      explanation: 'Khối lượng SI: kilogram (kg)'
    }
  ],

  // Lớp 12 - Bài 6: Nhiệt hóa hơi
  'lop-12-chuong-1-bai-6': [
    {
      id: 'l12-c1-b6-q1',
      question: 'Nhiệt hóa hơi kí hiệu?',
      answers: ['L', 'Q', 'c'],
      correctIndex: 0,
      explanation: 'Nhiệt hóa hơi kí hiệu: L'
    },
    {
      id: 'l12-c1-b6-q2',
      question: 'Đơn vị của L?',
      answers: ['J', 'J/kg', 'J/K'],
      correctIndex: 1,
      explanation: 'Nhiệt hóa hơi: J/kg'
    },
    {
      id: 'l12-c1-b6-q3',
      question: 'Công thức Q hóa hơi?',
      answers: ['Q = mL', 'Q = L/m', 'Q = mcΔt'],
      correctIndex: 0,
      explanation: 'Nhiệt lượng hóa hơi: Q = mL'
    },
    {
      id: 'l12-c1-b6-q4',
      question: 'Hóa hơi ở nhiệt độ?',
      answers: ['Sôi', 'Bất kỳ', 'Nóng chảy'],
      correctIndex: 0,
      explanation: 'Hóa hơi ở nhiệt độ sôi'
    },
    {
      id: 'l12-c1-b6-q5',
      question: 'Sôi khác bay hơi?',
      answers: ['Cùng tốc độ', 'Khác tốc độ', 'Giống nhau'],
      correctIndex: 1,
      explanation: 'Sôi nhanh hơn bay hơi'
    },
    {
      id: 'l12-c1-b6-q6',
      question: 'Ngưng tụ là quá trình?',
      answers: ['Hơi→Lỏng', 'Lỏng→Hơi', 'Rắn→Lỏng'],
      correctIndex: 0,
      explanation: 'Ngưng tụ: hơi → lỏng'
    },
    {
      id: 'l12-c1-b6-q7',
      question: 'Q ngưng tụ = ?',
      answers: ['mL', '-mL', '0'],
      correctIndex: 1,
      explanation: 'Ngưng tụ: Q = -mL (tỏa nhiệt)'
    },
    {
      id: 'l12-c1-b6-q8',
      question: 'Hóa hơi thu hay tỏa?',
      answers: ['Thu', 'Tỏa', 'Không đổi'],
      correctIndex: 0,
      explanation: 'Hóa hơi: thu nhiệt'
    },
    {
      id: 'l12-c1-b6-q9',
      question: 'L nước ở 100°C?',
      answers: ['2.26×10⁶J/kg', '3.34×10⁵J/kg', '4.18J/kg.K'],
      correctIndex: 0,
      explanation: 'L nước = 2.26×10⁶ J/kg'
    },
    {
      id: 'l12-c1-b6-q10',
      question: 'Nhiệt độ sôi phụ thuộc?',
      answers: ['Áp suất', 'Khối lượng', 'Thể tích'],
      correctIndex: 0,
      explanation: 'Nhiệt độ sôi phụ thuộc áp suất'
    }
  ]
}

/**
 * Lấy câu hỏi game cho một bài học cụ thể
 */
export function getGameQuestions(lop: number, chuong: number, bai: number): DoorQuestQuestion[] {
  const key = `lop-${lop}-chuong-${chuong}-bai-${bai}`
  if (minigameQuestionMap[key]) {
    return minigameQuestionMap[key]
  }
  return gameQuestions[key] || []
}

/**
 * Lấy cấu hình minigame (nếu có) cho bài học
 */
export function getGameQuestionConfig(
  lop: number,
  chuong: number,
  bai: number
): { minQuestions?: number; maxQuestions?: number } | undefined {
  const key = `lop-${lop}-chuong-${chuong}-bai-${bai}`
  return minigameConfigMap[key]
}

/**
 * Kiểm tra xem bài học có câu hỏi game không
 */
export function hasGameQuestions(lop: number, chuong: number, bai: number): boolean {
  const key = `lop-${lop}-chuong-${chuong}-bai-${bai}`
  if (minigameQuestionMap[key]?.length) {
    return true
  }
  const legacyQuestions = gameQuestions[key]
  return Array.isArray(legacyQuestions) && legacyQuestions.length > 0
}

/**
 * Lấy số lượng câu hỏi của một bài học
 */
export function getGameQuestionsCount(lop: number, chuong: number, bai: number): number {
  const key = `lop-${lop}-chuong-${chuong}-bai-${bai}`
  if (minigameQuestionMap[key]?.length) {
    return minigameQuestionMap[key].length
  }
  const legacyQuestions = gameQuestions[key]
  return Array.isArray(legacyQuestions) ? legacyQuestions.length : 0
}

